class WReviews extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.viewport = this.querySelector('.w-reviews__viewport');
    this.track = this.querySelector('.w-reviews__track');
    this.dots = Array.from(this.querySelectorAll('.w-reviews__dot'));
    this.cards = Array.from(this.track.children);
    this.count = this.cards.length;
    if (!this.viewport || !this.track || this.count <= 1) return;

    const { signal } = this.controller;
    this.loop = this.dataset.loop === 'true';
    this.index = 0;
    this.animating = false;

    if (this.loop) {
      this.cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        this.track.appendChild(clone);
      });
    }

    this.prevButton = this.querySelector('[data-direction="prev"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    this.prevButton?.addEventListener('click', () => this.move(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.move(1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.select(i), { signal }));
    this.track.addEventListener('transitionend', (event) => this.onTransitionEnd(event), { signal });
    this.viewportWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === this.viewportWidth) return;
      this.viewportWidth = window.innerWidth;
      this.animating = false;
      if (this.animTimer) clearTimeout(this.animTimer);
      this.setPosition(false);
      this.updateUI();
    }, { signal });

    let startX = null;
    this.viewport.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true, signal });
    this.viewport.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const delta = event.changedTouches[0].clientX - startX;
      startX = null;
      if (Math.abs(delta) > 40) this.move(delta < 0 ? 1 : -1);
    }, { passive: true, signal });

    this.setPosition(false);
    this.updateUI();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  step() {
    return this.cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
  }

  setPosition(animate) {
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = 'translateX(' + -this.index * this.step() + 'px)';
    if (!animate) {
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }
  }

  activeDot() {
    return ((this.index % this.count) + this.count) % this.count;
  }

  visibleCount() {
    return Math.max(1, Math.floor((this.viewport.clientWidth + 2) / this.step()));
  }

  maxIndex() {
    return Math.max(0, this.count - this.visibleCount());
  }

  updateUI() {
    const active = this.activeDot();
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    if (!this.loop) {
      if (this.prevButton) this.prevButton.disabled = this.index <= 0;
      if (this.nextButton) this.nextButton.disabled = this.index >= this.maxIndex();
    }
  }

  move(direction) {
    if (this.animating) return;
    if (this.loop) {
      if (direction === -1 && this.index === 0) {
        this.index = this.count;
        this.setPosition(false);
      }
      this.animateTo(this.index + direction);
      return;
    }
    this.animateTo(Math.max(0, Math.min(this.index + direction, this.maxIndex())));
  }

  select(index) {
    if (this.animating || index === this.activeDot()) return;
    this.animateTo(index);
  }

  animateTo(index) {
    if (index === this.index) return;
    this.animating = true;
    this.index = index;
    this.setPosition(true);
    this.updateUI();
    if (this.animTimer) clearTimeout(this.animTimer);
    this.animTimer = setTimeout(() => { this.animating = false; }, 600);
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.animating = false;
    if (this.loop && this.index >= this.count) {
      this.index -= this.count;
      this.setPosition(false);
      this.updateUI();
    }
  }
}

customElements.define('w-reviews', WReviews);
